import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '../../../adminComponents/FormGrid/FormGrid';
import { ImagePlaceholderCircle } from '../../../assets';
import { Button } from '../../../components';
import { ModalFullScreen } from '../../../components/modalFullscreen/modalFullscreen';
import { centsToDollars, formatter } from '../../../helpers';
import { Box, Div, Img, Pill, Tab } from '../../../styles/styles';
import { Disclaimer, H3, H5, P } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';
import { MenuItemV2Response, MenuV2Response } from '../../../types/menu.types';
import { ErrorToast } from '../../../adminComponents/Toasts/Toasts';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';
import { SearchInput } from '../../../adminComponents/AdminInput/SearchInput';
import { ItemListImage } from '../MenyStyled';

export const ImportPosItemsModal = ({
  isOpen,
  onClose,
  posItemsImport,
  handleImportPosItems,
  menu,
}: {
  isOpen: boolean;
  onClose: () => void;
  posItemsImport: MenuV2Response | null;
  handleImportPosItems: (categoryIds: number[]) => void;
  menu: MenuV2Response | null;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [alreadyImportedCategoryIds, setAlreadyImportedCategoryIds] = useState<
    number[]
  >([]);

  const handleCategorySearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCategorySearchQuery(e.target.value);
    },
    []
  );

  const handleItemSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setItemSearchQuery(e.target.value);
    },
    []
  );

  const importedPosCategoryIds: number[] = useMemo(() => {
    if (!menu) return [];
    return menu.categories
      .map((category) => category.posId)
      .filter((id): id is number => id != null);
  }, [menu]);

  useEffect(() => {
    if (!posItemsImport) {
      setSelectedCategory(null);
      setSelectedCategoryIds([]);
      setAlreadyImportedCategoryIds([]);
      setCategorySearchQuery('');
      setItemSearchQuery('');
      return;
    }

    const defaultCategoryId = posItemsImport.categories[0]?.id ?? null;
    setSelectedCategory(defaultCategoryId);

    const alreadyImported = posItemsImport.categories
      .map((category) => category.id)
      .filter((id) => importedPosCategoryIds.includes(id));

    setAlreadyImportedCategoryIds(alreadyImported);
    setSelectedCategoryIds(alreadyImported);
  }, [posItemsImport, importedPosCategoryIds]);

  const filteredCategories = useMemo(() => {
    if (!posItemsImport) return [];

    if (!categorySearchQuery.trim()) {
      return posItemsImport.categories;
    }

    const query = categorySearchQuery.toLowerCase().trim();
    return posItemsImport.categories.filter((category) =>
      category.name.toLowerCase().includes(query)
    );
  }, [posItemsImport, categorySearchQuery]);

  useEffect(() => {
    if (!posItemsImport) return;

    if (filteredCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    const selectedStillVisible = filteredCategories.some(
      (c) => c.id === selectedCategory
    );
    if (!selectedStillVisible) {
      setSelectedCategory(filteredCategories[0].id);
    }
  }, [filteredCategories, posItemsImport, selectedCategory]);

  const filteredItems = useMemo(() => {
    if (!posItemsImport || selectedCategory === null) return [];

    const category = posItemsImport.categories.find(
      (category) => category.id === selectedCategory
    );
    if (!category) return [];

    if (!itemSearchQuery.trim()) {
      return category.items;
    }

    const query = itemSearchQuery.toLowerCase().trim();
    return category.items.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
    );
  }, [posItemsImport, selectedCategory, itemSearchQuery]);

  const handleCategoryToggle = useCallback(
    (categoryId: number) => {
      if (alreadyImportedCategoryIds.includes(categoryId)) {
        ErrorToast(`This category is already imported`);
        return;
      }
      setSelectedCategoryIds((prev) =>
        prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId]
      );
    },
    [alreadyImportedCategoryIds]
  );

  const HandleSubmit = useCallback(() => {
    handleImportPosItems(selectedCategoryIds);
  }, [selectedCategoryIds, handleImportPosItems]);

  const HandleCancel = useCallback(() => {
    onClose();
    setSelectedCategoryIds([]);
    setSelectedCategory(null);
    setCategorySearchQuery('');
    setItemSearchQuery('');
  }, [onClose]);

  return (
    <ModalFullScreen
      height="calc(100vh - 150px)"
      isOpen={isOpen}
      closeModal={onClose}
      footer={
        <Div $flex $gap="16px" $justifyEnd $widthSM="100%">
          <Button $selected $gray $w="200px" $wSM="100%" onClick={HandleCancel}>
            Cancel
          </Button>
          <Button $selected $w="200px" $wSM="100%" onClick={HandleSubmit}>
            Confirm
          </Button>
        </Div>
      }
      exitButton={false}
      $numOfFloatingButtons={1}
    >
      <Div $flex $between $alignCenter $alignStartSM $gap="16px">
        <Div>
          <H3 $m="0" $mrSM="32">
            Categories
          </H3>
          <Disclaimer $m="0">
            Select categories to import (Total selected:{' '}
            {selectedCategoryIds.length})
          </Disclaimer>
        </Div>

        <SearchInput
          value={categorySearchQuery}
          maxWidth="250px"
          maxWidthSM="200px"
          placeholder="Search Categories"
          onChange={handleCategorySearchChange}
        />
      </Div>

      <Div $flex $gap="12px 16px" $wrap $mb="24" $m="16px 0">
        {filteredCategories.map((category) => {
          const isAlreadyImported = alreadyImportedCategoryIds.includes(
            category.id
          );
          const isChecked = selectedCategoryIds.includes(category.id);
          return (
            <Tab
              key={category.id}
              selected={category.id === selectedCategory}
              onClick={() => setSelectedCategory(category.id)}
              $minWidthAutoSM
              $minWidthAuto
              $minWidthAutoMD
              $paddingSmall="0 4px 0 16px"
              $padding="0 12px 0 16px"
              $smallSM
            >
              <Div $flex $alignCenter $gap="12px" $gapSM="8px">
                <span>{category.name}</span>
                {isAlreadyImported ? (
                  <Pill $background={lightTheme.green}>
                    <Div $flex $alignCenter $gap="8px">
                      <Disclaimer $color={lightTheme.white} $m="0">
                        Imported{' '}
                      </Disclaimer>
                      {/* <Icon
                        $size="12"
                        $pointer
                        onClick={() => handleCategoryToggle(category.id)}
                        color={lightTheme.white}
                      >
                        <X />
                      </Icon> */}
                    </Div>
                  </Pill>
                ) : isChecked ? (
                  <Pill
                    $minWidthSM="70px"
                    $background={lightTheme.primary}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryToggle(category.id);
                    }}
                  >
                    <Disclaimer $color={lightTheme.white} $m="0">
                      Selected
                    </Disclaimer>
                  </Pill>
                ) : (
                  <Pill
                    $minWidthSM="70px"
                    $border={lightTheme.lightGray}
                    $background="none"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryToggle(category.id);
                    }}
                  >
                    <Disclaimer $color={lightTheme.darkGray} $m="0">
                      Select
                    </Disclaimer>
                  </Pill>
                )}
              </Div>
            </Tab>
          );
        })}
      </Div>

      <Div $flex $between $alignCenter $alignStartSM $gap="16px" $m="16px 0">
        <Div $mrSM="32">
          <H3 $m="0">Items</H3>
          <Disclaimer $m="0">
            Preview of items in the selected category
          </Disclaimer>
        </Div>

        <div style={{ flex: 'none' }}>
          <SearchInput
            value={itemSearchQuery}
            maxWidth="250px"
            maxWidthSM="200px"
            placeholder="Search Items"
            onChange={handleItemSearchChange}
          />
        </div>
      </Div>

      <Grid $gap="16px" $gapMD="12px" $gapSM="8px">
        {selectedCategory !== null &&
          filteredItems.map((item) => <MenuItem key={item.id} item={item} />)}
      </Grid>
      {(filteredCategories.length === 0 || filteredItems.length === 0) && (
        <NoDataComponent
          title={
            filteredCategories.length === 0
              ? 'No categories match your search'
              : 'No items found in this category'
          }
          description={
            filteredCategories.length === 0
              ? 'Try a different search or clear the category filter'
              : 'Please try again with a different search query or select a different category'
          }
          height="calc(100vh - 550px)"
          heightSM="auto"
        />
      )}
    </ModalFullScreen>
  );
};

const MenuItem = ({ item }: { item: MenuItemV2Response }) => {
  return (
    <Box $height="fit-content" key={item.id}>
      <Div $flex $gap="16px" $gapSM="8px" $between $alignStart>
        <Div $flex $gap="16px" $gapSM="8px">
          <Div $relative>
            <Div $flex>
              {item.imageUrl ? (
                <ItemListImage>
                  <Img alt="" src={item.imageUrl} />
                </ItemListImage>
              ) : (
                <>
                  <Div $desktop>
                    <ImagePlaceholderCircle width="60px" height="60px" />
                  </Div>
                  <Div $mobile>
                    <ImagePlaceholderCircle width="50px" height="50px" />
                  </Div>
                </>
              )}
            </Div>
          </Div>
          <Div $flex $column $gap="2px" $justifyCenter>
            <Div $desktop>
              <H5 $mb="0">
                <span>
                  {formatter.format(centsToDollars(item.price ?? 0))} -{' '}
                  {item.name}
                </span>
              </H5>
            </Div>
            <Div $mobile>
              <P $mb="0" $bold>
                <span>
                  {formatter.format(centsToDollars(item.price ?? 0))} -{' '}
                  {item.name}
                </span>
              </P>
            </Div>
            <Div $desktop>
              <P $m="0">
                {item.description && <P $m="0">{item.description}</P>}
              </P>
            </Div>
          </Div>
        </Div>
      </Div>

      <Div $mobile $mt="4">
        <P $m="0">{item.description && <P $m="0">{item.description}</P>}</P>
      </Div>
    </Box>
  );
};
